import { useState, useEffect, useCallback } from "react"
import { auth } from "../components/Authenticator/firebase"
import { useNavigate } from "react-router-dom"
import { API_URL } from "../config/api"
import { 
  Users, 
  MessageSquare, 
  Mail, 
  Search, 
  ChevronLeft, 
  ChevronRight,
  Calendar,
  Download,
  Image,
  FileText,
  Sheet,
  User,
  Bot,
  Lock,
  Home,
  Shield,
  Building2,
  Check,
  X,
  AlertCircle,
  UserCheck,
  RefreshCw
} from "lucide-react"

export default function PrivacyChats() {
  const navigate = useNavigate()
  
  // Tab state
  const [activeTab, setActiveTab] = useState('chats') // 'chats' or 'roles'
  
  // Chat states
  const [users, setUsers] = useState([])
  const [selectedUser, setSelectedUser] = useState(null)
  const [conversations, setConversations] = useState([])
  const [selectedConversation, setSelectedConversation] = useState(null)
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingConversations, setLoadingConversations] = useState(false)
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [stats, setStats] = useState({ users: 0, conversations: 0, messages: 0 })
  
  // Roles states
  const [rolesUsers, setRolesUsers] = useState([])
  const [areas, setAreas] = useState([])
  const [selectedRoleUser, setSelectedRoleUser] = useState(null)
  const [loadingRoles, setLoadingRoles] = useState(false)
  const [loadingAreas, setLoadingAreas] = useState(false)
  const [savingRole, setSavingRole] = useState(false)
  const [rolesSearchTerm, setRolesSearchTerm] = useState("")
  const [filterArea, setFilterArea] = useState('all')
  const [roleMessage, setRoleMessage] = useState(null)
  
  const [mobileView, setMobileView] = useState('users')
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 900)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // ==================== CHAT FUNCTIONS ====================
  
  const loadUsers = useCallback(async () => {
    try {
      setLoading(true)
      const token = await auth.currentUser?.getIdToken()
      if (!token) {
        navigate("/login")
        return
      }

      const [usersRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/users`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/api/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        })
      ])

      if (usersRes.status === 403) {
        navigate("/home")
        return
      }

      const usersData = await usersRes.json()
      const statsData = await statsRes.json()

      if (usersData.success) setUsers(usersData.users || [])
      if (statsData.success) setStats(statsData.stats)
    } catch (error) {
      console.error("Error:", error)
    } finally {
      setLoading(false)
    }
  }, [navigate])

  const loadUserConversations = async (user) => {
    try {
      setLoadingConversations(true)
      setSelectedUser(user)
      setSelectedConversation(null)
      setMessages([])
      if (isMobile) setMobileView('conversations')

      const token = await auth.currentUser?.getIdToken()
      const response = await fetch(
        `${API_URL}/api/admin/user/${user.uid}/conversations`,
        { headers: { Authorization: `Bearer ${token}` } }
      )

      const data = await response.json()
      if (data.success) setConversations(data.conversations || [])
    } catch (error) {
      console.error("Error:", error)
    } finally {
      setLoadingConversations(false)
    }
  }

  const loadMessages = async (conversation) => {
    try {
      setLoadingMessages(true)
      setSelectedConversation(conversation)
      if (isMobile) setMobileView('messages')

      const token = await auth.currentUser?.getIdToken()
      const response = await fetch(
        `${API_URL}/api/admin/conversation/${selectedUser.uid}/${conversation.id}/messages`,
        { headers: { Authorization: `Bearer ${token}` } }
      )

      const data = await response.json()
      if (data.success) setMessages(data.messages || [])
    } catch (error) {
      console.error("Error:", error)
    } finally {
      setLoadingMessages(false)
    }
  }

  // ==================== ROLES FUNCTIONS ====================
  
  const loadRolesData = useCallback(async () => {
    try {
      setLoadingRoles(true)
      setLoadingAreas(true)
      const token = await auth.currentUser?.getIdToken()
      if (!token) return

      const [usersRes, areasRes] = await Promise.all([
        fetch(`${API_URL}/api/roles/users`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/api/roles/areas`, {
          headers: { Authorization: `Bearer ${token}` },
        })
      ])

      const usersData = await usersRes.json()
      const areasData = await areasRes.json()

      if (usersData.success) setRolesUsers(usersData.users || [])
      if (areasData.success) setAreas(areasData.areas || [])
    } catch (error) {
      console.error("Error loading roles:", error)
    } finally {
      setLoadingRoles(false)
      setLoadingAreas(false)
    }
  }, [])

  const assignArea = async (userId, areaId) => {
    try {
      setSavingRole(true)
      const token = await auth.currentUser?.getIdToken()
      
      const response = await fetch(`${API_URL}/api/roles/user/${userId}/assign`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ areaId })
      })

      const data = await response.json()
      
      if (data.success) {
        setRoleMessage({ type: 'success', text: `Asignado a: ${data.areaName}` })
        setRolesUsers(prev => prev.map(u => 
          u.uid === userId 
            ? { ...u, areaId, areaName: data.areaName }
            : u
        ))
        if (selectedRoleUser?.uid === userId) {
          setSelectedRoleUser(prev => ({ ...prev, areaId, areaName: data.areaName }))
        }
      } else {
        setRoleMessage({ type: 'error', text: data.error || 'Error al asignar' })
      }
    } catch (error) {
      setRoleMessage({ type: 'error', text: 'Error de conexión' })
    } finally {
      setSavingRole(false)
      setTimeout(() => setRoleMessage(null), 3000)
    }
  }

  const removeRole = async (userId) => {
    try {
      setSavingRole(true)
      const token = await auth.currentUser?.getIdToken()
      
      const response = await fetch(`${API_URL}/api/roles/user/${userId}/role`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      })

      const data = await response.json()
      
      if (data.success) {
        setRoleMessage({ type: 'success', text: 'Rol eliminado' })
        setRolesUsers(prev => prev.map(u => 
          u.uid === userId 
            ? { ...u, areaId: null, areaName: 'Sin asignar' }
            : u
        ))
        if (selectedRoleUser?.uid === userId) {
          setSelectedRoleUser(prev => ({ ...prev, areaId: null, areaName: 'Sin asignar' }))
        }
      }
    } catch (error) {
      setRoleMessage({ type: 'error', text: 'Error de conexión' })
    } finally {
      setSavingRole(false)
      setTimeout(() => setRoleMessage(null), 3000)
    }
  }

  // ==================== EFFECTS ====================

  useEffect(() => {
    loadUsers()
  }, [loadUsers])

  useEffect(() => {
    if (activeTab === 'roles' && rolesUsers.length === 0) {
      loadRolesData()
    }
  }, [activeTab, rolesUsers.length, loadRolesData])

  // ==================== HELPERS ====================

  const goBack = () => {
    if (activeTab === 'chats') {
      if (mobileView === 'messages') setMobileView('conversations')
      else if (mobileView === 'conversations') {
        setMobileView('users')
        setSelectedUser(null)
      }
    } else {
      setMobileView('users')
      setSelectedRoleUser(null)
    }
  }

  const filteredUsers = users.filter(user => {
    const search = searchTerm.toLowerCase()
    return (
      user.displayName?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search) ||
      user.uid?.toLowerCase().includes(search)
    )
  })

  const filteredRolesUsers = rolesUsers.filter(user => {
    const search = rolesSearchTerm.toLowerCase()
    const matchesSearch = (
      user.displayName?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search)
    )
    const matchesArea = filterArea === 'all' || 
      (filterArea === 'unassigned' && !user.areaId) ||
      user.areaId === filterArea
    return matchesSearch && matchesArea
  })

  const formatDate = (dateStr) => {
    if (!dateStr) return "-"
    const date = new Date(dateStr)
    return date.toLocaleString("es-ES", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit"
    })
  }

  const formatFullDate = (dateStr) => {
    if (!dateStr) return "-"
    const date = new Date(dateStr)
    return date.toLocaleString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    })
  }

  const getInitials = (name) => {
    if (!name || name === "Sin nombre") return "?"
    return name.split(" ").map(n => n[0]).join("").toUpperCase().substring(0, 2)
  }

  const getAvatarColor = (uid) => {
    const colors = ["#6366f1", "#8b5cf6", "#ec4899", "#f43f5e", "#f97316", "#eab308", "#22c55e", "#14b8a6", "#0ea5e9", "#3b82f6"]
    return colors[uid?.charCodeAt(0) % colors.length || 0]
  }

  const getAreaColor = (areaId) => {
    const colors = {
      'direccion': '#dc2626', 'socios': '#ea580c', 'rrhh': '#d97706',
      'administracion': '#65a30d', 'contabilidad': '#16a34a', 'facturacion': '#059669',
      'recepcion': '#0d9488', 'informatica': '#0891b2', 'produccion': '#0284c7',
      'produccion_encargos': '#2563eb', 'mantenimiento': '#4f46e5', 'calidad': '#7c3aed',
      'tecnico': '#9333ea', 'riegos': '#c026d3', 'sector_injertos': '#db2777',
      'sector_siembra': '#e11d48', 'lavado_bandejas': '#be123c', 'logistica': '#b91c1c',
      'cargas': '#c2410c', 'sector_choferes': '#a16207', 'garden': '#4d7c0f',
      'atencion_garden': '#15803d', 'mantenimiento_jardines': '#047857'
    }
    return colors[areaId] || '#6b7280'
  }

  const panelVisible = (panel) => {
    if (!isMobile) return true
    return mobileView === panel
  }

  const rolesStats = {
    total: rolesUsers.length,
    assigned: rolesUsers.filter(u => u.areaId).length,
    unassigned: rolesUsers.filter(u => !u.areaId).length
  }

  // ==================== RENDER ====================

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      height: "100vh",
      background: "#fbf9f4",
      fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
      overflow: "hidden"
    }}>
      {/* Mensaje de notificación */}
      {roleMessage && (
        <div style={{
          position: "fixed",
          top: "20px",
          right: "20px",
          padding: "12px 18px",
          borderRadius: "10px",
          background: roleMessage.type === 'success' ? "#dcfce7" : "#fee2e2",
          color: roleMessage.type === 'success' ? "#166534" : "#dc2626",
          border: `1px solid ${roleMessage.type === 'success' ? "#bbf7d0" : "#fecaca"}`,
          display: "flex",
          alignItems: "center",
          gap: "8px",
          zIndex: 1000,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          fontSize: "13px",
          fontWeight: "500"
        }}>
          {roleMessage.type === 'success' ? <Check size={16} /> : <AlertCircle size={16} />}
          {roleMessage.text}
        </div>
      )}
      
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      {/* TABS - Pestañas principales */}
      <div style={{
        display: "flex",
        background: "#fff",
        borderBottom: "1px solid #e5e2dc",
        padding: "0 20px",
        flexShrink: 0
      }}>
        <button
          onClick={() => { setActiveTab('chats'); setMobileView('users'); }}
          style={{
            padding: "16px 24px",
            background: "none",
            border: "none",
            borderBottom: activeTab === 'chats' ? "3px solid #374151" : "3px solid transparent",
            color: activeTab === 'chats' ? "#1f2937" : "#6b7280",
            fontWeight: activeTab === 'chats' ? "600" : "500",
            fontSize: "14px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            transition: "all 0.2s"
          }}
        >
          <MessageSquare size={18} />
          Conversaciones
        </button>
        <button
          onClick={() => { setActiveTab('roles'); setMobileView('users'); setSelectedRoleUser(null); }}
          style={{
            padding: "16px 24px",
            background: "none",
            border: "none",
            borderBottom: activeTab === 'roles' ? "3px solid #7c3aed" : "3px solid transparent",
            color: activeTab === 'roles' ? "#7c3aed" : "#6b7280",
            fontWeight: activeTab === 'roles' ? "600" : "500",
            fontSize: "14px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            transition: "all 0.2s"
          }}
        >
          <Shield size={18} />
          Gestión de Roles
        </button>
        <div style={{ flex: 1 }} />
        <button
          onClick={() => navigate("/home")}
          style={{
            background: "none",
            border: "none",
            padding: "16px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            color: "#6b7280"
          }}
        >
          <Home size={20} />
        </button>
      </div>

      {/* CONTENT */}
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        
        {/* ==================== TAB: CHATS ==================== */}
        {activeTab === 'chats' && (
          <>
            {/* Panel 1: Usuarios */}
            {panelVisible('users') && (
              <div style={{
                width: isMobile ? "100%" : "340px",
                minWidth: isMobile ? "100%" : "340px",
                borderRight: isMobile ? "none" : "1px solid #e5e2dc",
                display: "flex",
                flexDirection: "column",
                background: "#fff"
              }}>
                <div style={{ padding: "20px", borderBottom: "1px solid #f0ebe3" }}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginBottom: "20px"
                  }}>
                    <div style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "10px",
                      background: "linear-gradient(135deg, #374151 0%, #1f2937 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}>
                      <Lock size={18} color="#fff" />
                    </div>
                    <div>
                      <h1 style={{ fontSize: "15px", fontWeight: "600", color: "#1f2937", margin: 0 }}>
                        Panel Admin
                      </h1>
                      <p style={{ fontSize: "11px", color: "#6b7280", margin: 0 }}>
                        Gestión de conversaciones
                      </p>
                    </div>
                  </div>

                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: "10px",
                    marginBottom: "16px"
                  }}>
                    <div style={{
                      background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                      padding: "14px 12px",
                      borderRadius: "12px",
                      textAlign: "center",
                      border: "1px solid #bfdbfe"
                    }}>
                      <Users size={18} color="#2563eb" style={{ marginBottom: "4px" }} />
                      <div style={{ fontSize: "20px", fontWeight: "700", color: "#1e40af" }}>{stats.users}</div>
                      <div style={{ fontSize: "10px", color: "#3b82f6", fontWeight: "500" }}>Usuarios</div>
                    </div>
                    <div style={{
                      background: "linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)",
                      padding: "14px 12px",
                      borderRadius: "12px",
                      textAlign: "center",
                      border: "1px solid #e9d5ff"
                    }}>
                      <MessageSquare size={18} color="#7c3aed" style={{ marginBottom: "4px" }} />
                      <div style={{ fontSize: "20px", fontWeight: "700", color: "#6d28d9" }}>{stats.conversations}</div>
                      <div style={{ fontSize: "10px", color: "#8b5cf6", fontWeight: "500" }}>Chats</div>
                    </div>
                    <div style={{
                      background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
                      padding: "14px 12px",
                      borderRadius: "12px",
                      textAlign: "center",
                      border: "1px solid #bbf7d0"
                    }}>
                      <Mail size={18} color="#16a34a" style={{ marginBottom: "4px" }} />
                      <div style={{ fontSize: "20px", fontWeight: "700", color: "#15803d" }}>{stats.messages}</div>
                      <div style={{ fontSize: "10px", color: "#22c55e", fontWeight: "500" }}>Mensajes</div>
                    </div>
                  </div>

                  <div style={{ position: "relative" }}>
                    <Search size={16} color="#9ca3af" style={{
                      position: "absolute",
                      left: "12px",
                      top: "50%",
                      transform: "translateY(-50%)"
                    }} />
                    <input
                      type="text"
                      placeholder="Buscar usuarios..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "11px 12px 11px 38px",
                        border: "1px solid #e5e7eb",
                        borderRadius: "10px",
                        fontSize: "13px",
                        boxSizing: "border-box",
                        background: "#f9fafb",
                        outline: "none"
                      }}
                    />
                  </div>
                </div>

                <div style={{ flex: 1, overflowY: "auto" }}>
                  {loading ? (
                    <div style={{ padding: "60px 20px", textAlign: "center", color: "#9ca3af" }}>
                      <div style={{
                        width: "32px",
                        height: "32px",
                        border: "3px solid #e5e7eb",
                        borderTopColor: "#374151",
                        borderRadius: "50%",
                        margin: "0 auto 12px",
                        animation: "spin 1s linear infinite"
                      }} />
                      Cargando usuarios...
                    </div>
                  ) : filteredUsers.length === 0 ? (
                    <div style={{ padding: "60px 20px", textAlign: "center", color: "#9ca3af" }}>
                      <Users size={32} style={{ marginBottom: "12px", opacity: 0.4 }} />
                      <div>No hay usuarios</div>
                    </div>
                  ) : (
                    filteredUsers.map(user => (
                      <div
                        key={user.uid}
                        onClick={() => loadUserConversations(user)}
                        style={{
                          padding: "14px 20px",
                          borderBottom: "1px solid #f3f4f6",
                          cursor: "pointer",
                          background: selectedUser?.uid === user.uid ? "#f9fafb" : "transparent",
                          transition: "all 0.15s",
                          display: "flex",
                          alignItems: "center",
                          gap: "12px"
                        }}
                      >
                        <div style={{
                          width: "44px",
                          height: "44px",
                          borderRadius: "12px",
                          background: user.photoURL ? `url(${user.photoURL}) center/cover` : getAvatarColor(user.uid),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          fontSize: "15px",
                          fontWeight: "600",
                          flexShrink: 0
                        }}>
                          {!user.photoURL && getInitials(user.displayName)}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: "14px", fontWeight: "600", color: "#1f2937", marginBottom: "3px" }}>
                            {user.displayName || "Sin nombre"}
                          </div>
                          <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "4px" }}>
                            {user.email}
                          </div>
                          <span style={{
                            background: user.conversationCount > 0 ? "#dbeafe" : "#f3f4f6",
                            color: user.conversationCount > 0 ? "#2563eb" : "#9ca3af",
                            padding: "2px 6px",
                            borderRadius: "4px",
                            fontSize: "11px",
                            fontWeight: "500"
                          }}>
                            {user.conversationCount} chats
                          </span>
                        </div>
                        <ChevronRight size={18} color="#d1d5db" />
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Panel 2: Conversaciones */}
            {panelVisible('conversations') && (
              <div style={{
                width: isMobile ? "100%" : "320px",
                minWidth: isMobile ? "100%" : "320px",
                borderRight: isMobile ? "none" : "1px solid #e5e2dc",
                display: "flex",
                flexDirection: "column",
                background: "#fff"
              }}>
                {!selectedUser ? (
                  <div style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    color: "#9ca3af",
                    padding: "40px"
                  }}>
                    <ChevronLeft size={48} style={{ marginBottom: "12px", opacity: 0.3 }} />
                    <div style={{ textAlign: "center", fontSize: "13px" }}>Selecciona un usuario</div>
                  </div>
                ) : (
                  <>
                    <div style={{ padding: "16px 20px", borderBottom: "1px solid #f0ebe3", background: "#fafaf8" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        {isMobile && (
                          <button onClick={goBack} style={{ background: "none", border: "none", cursor: "pointer", padding: "4px", display: "flex" }}>
                            <ChevronLeft size={22} color="#6b7280" />
                          </button>
                        )}
                        <div style={{
                          width: "36px",
                          height: "36px",
                          borderRadius: "10px",
                          background: selectedUser.photoURL ? `url(${selectedUser.photoURL}) center/cover` : getAvatarColor(selectedUser.uid),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          fontSize: "13px",
                          fontWeight: "600"
                        }}>
                          {!selectedUser.photoURL && getInitials(selectedUser.displayName)}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: "14px", fontWeight: "600", color: "#1f2937" }}>{selectedUser.displayName || "Sin nombre"}</div>
                          <div style={{ fontSize: "11px", color: "#6b7280" }}>{selectedUser.email}</div>
                        </div>
                      </div>
                    </div>
                    <div style={{ flex: 1, overflowY: "auto" }}>
                      {loadingConversations ? (
                        <div style={{ padding: "60px 20px", textAlign: "center", color: "#9ca3af" }}>Cargando...</div>
                      ) : conversations.length === 0 ? (
                        <div style={{ padding: "60px 20px", textAlign: "center", color: "#9ca3af" }}>
                          <MessageSquare size={32} style={{ marginBottom: "12px", opacity: 0.3 }} />
                          <div>Sin conversaciones</div>
                        </div>
                      ) : (
                        conversations.map(conv => (
                          <div
                            key={conv.id}
                            onClick={() => loadMessages(conv)}
                            style={{
                              padding: "14px 20px",
                              borderBottom: "1px solid #f3f4f6",
                              cursor: "pointer",
                              background: selectedConversation?.id === conv.id ? "#eff6ff" : "transparent",
                              display: "flex",
                              alignItems: "center",
                              gap: "12px"
                            }}
                          >
                            <div style={{
                              width: "40px",
                              height: "40px",
                              borderRadius: "10px",
                              background: selectedConversation?.id === conv.id ? "#2563eb" : "#f3f4f6",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center"
                            }}>
                              <MessageSquare size={18} color={selectedConversation?.id === conv.id ? "#fff" : "#6b7280"} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: "13px", fontWeight: "500", color: "#1f2937", marginBottom: "4px" }}>
                                {conv.title || "Sin título"}
                              </div>
                              <div style={{ fontSize: "11px", color: "#9ca3af", display: "flex", justifyContent: "space-between" }}>
                                <span>{conv.messageCount} mensajes</span>
                                <span>{formatDate(conv.updatedAt)}</span>
                              </div>
                            </div>
                            <ChevronRight size={16} color="#d1d5db" />
                          </div>
                        ))
                      )}
                    </div>
                    <div style={{ padding: "14px 20px", borderTop: "1px solid #f0ebe3", fontSize: "12px", color: "#6b7280", background: "#fafaf8" }}>
                      {conversations.length} conversaciones
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Panel 3: Mensajes */}
            {panelVisible('messages') && (
              <div style={{ flex: 1, display: "flex", flexDirection: "column", background: "#fbf9f4", width: isMobile ? "100%" : "auto", minWidth: 0 }}>
                {!selectedConversation ? (
                  <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", color: "#9ca3af", padding: "40px" }}>
                    <MessageSquare size={56} style={{ marginBottom: "16px", opacity: 0.2 }} />
                    <div style={{ textAlign: "center", fontSize: "14px", color: "#6b7280" }}>Selecciona una conversación</div>
                  </div>
                ) : (
                  <>
                    <div style={{ padding: "18px 24px", borderBottom: "1px solid #e5e2dc", background: "#fff", flexShrink: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        {isMobile && (
                          <button onClick={goBack} style={{ background: "none", border: "none", cursor: "pointer", padding: "4px", display: "flex" }}>
                            <ChevronLeft size={20} color="#6b7280" />
                          </button>
                        )}
                        <div style={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "10px",
                          background: "linear-gradient(135deg, #374151 0%, #1f2937 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}>
                          <MessageSquare size={18} color="#fff" />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: "15px", fontWeight: "600", color: "#1f2937", marginBottom: "2px" }}>{selectedConversation.title}</div>
                          <div style={{ fontSize: "12px", color: "#6b7280", display: "flex", alignItems: "center", gap: "12px" }}>
                            <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                              <Calendar size={12} />
                              {formatFullDate(selectedConversation.createdAt)}
                            </span>
                            <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                              <MessageSquare size={12} />
                              {messages.length} msgs
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div style={{ flex: 1, overflowY: "auto", padding: "24px 28px", minHeight: 0 }}>
                      {loadingMessages ? (
                        <div style={{ textAlign: "center", color: "#9ca3af", padding: "60px" }}>Cargando mensajes...</div>
                      ) : messages.length === 0 ? (
                        <div style={{ textAlign: "center", color: "#9ca3af", padding: "60px" }}>No hay mensajes</div>
                      ) : (
                        messages.map((msg, index) => (
                          <div key={index} style={{ marginBottom: "24px", display: "flex", flexDirection: "column", alignItems: msg.role === "user" ? "flex-end" : "flex-start", width: "100%" }}>
                            <div style={{ fontSize: "11px", color: "#9ca3af", marginBottom: "6px", display: "flex", alignItems: "center", gap: "4px" }}>
                              {msg.role === "user" ? <User size={12} /> : <Bot size={12} />}
                              <span style={{ fontWeight: "500" }}>{msg.role === "user" ? "Usuario" : "IA"}</span>
                              {msg.timestamp && (<><span>·</span><span>{formatDate(msg.timestamp)}</span></>)}
                            </div>
                            <div style={{
                              maxWidth: "75%",
                              padding: "14px 18px",
                              borderRadius: msg.role === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                              background: msg.role === "user" ? "linear-gradient(135deg, #374151 0%, #1f2937 100%)" : "#fff",
                              color: msg.role === "user" ? "#fff" : "#374151",
                              fontSize: "14px",
                              lineHeight: "1.5",
                              boxShadow: msg.role === "user" ? "0 4px 12px rgba(31, 41, 55, 0.15)" : "0 2px 8px rgba(0,0,0,0.06)",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word"
                            }}>
                              {msg.content}
                            </div>
                            {msg.imageUrl && (
                              <a href={msg.imageUrl} target="_blank" rel="noopener noreferrer" style={{ marginTop: "10px", maxWidth: "75%", display: "block" }}>
                                <img src={msg.imageUrl} alt={msg.imageName || "Imagen"} style={{ maxWidth: "100%", maxHeight: "220px", borderRadius: "10px", border: "1px solid #e5e7eb" }} />
                                <div style={{ fontSize: "10px", color: "#6b7280", marginTop: "4px", display: "flex", alignItems: "center", gap: "4px" }}>
                                  <Image size={10} />{msg.imageName || "Imagen"}
                                </div>
                              </a>
                            )}
                            {msg.documentUrl && (
                              <a href={msg.documentUrl} target="_blank" rel="noopener noreferrer" style={{ marginTop: "10px", display: "inline-flex", alignItems: "center", gap: "10px", background: "#fff", border: "1px solid #e5e7eb", padding: "12px 16px", borderRadius: "10px", textDecoration: "none", color: "#374151" }}>
                                <div style={{ width: "36px", height: "36px", borderRadius: "8px", background: "#faf5ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <FileText size={18} color="#7c3aed" />
                                </div>
                                <div>
                                  <div style={{ fontWeight: "500", fontSize: "13px" }}>{msg.documentName || "Documento"}</div>
                                  <div style={{ fontSize: "11px", color: "#9ca3af" }}>Click para ver</div>
                                </div>
                              </a>
                            )}
                            {msg.excelFileUrl && (
                              <a href={msg.excelFileUrl} target="_blank" rel="noopener noreferrer" download={msg.excelFileName} style={{ marginTop: "10px", display: "inline-flex", alignItems: "center", gap: "10px", background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)", border: "1px solid #bbf7d0", padding: "12px 16px", borderRadius: "10px", textDecoration: "none", color: "#166534" }}>
                                <div style={{ width: "36px", height: "36px", borderRadius: "8px", background: "#22c55e", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <Sheet size={18} color="#fff" />
                                </div>
                                <div>
                                  <div style={{ fontWeight: "500", fontSize: "13px" }}>{msg.excelFileName || "Excel"}</div>
                                  <div style={{ fontSize: "11px", color: "#16a34a" }}>{msg.excelFileRows && msg.excelFileColumns ? `${msg.excelFileRows}×${msg.excelFileColumns}` : "Descargar"}</div>
                                </div>
                                <Download size={16} color="#16a34a" />
                              </a>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}

        {/* ==================== TAB: ROLES ==================== */}
        {activeTab === 'roles' && (
          <>
            {/* Panel 1: Lista de usuarios para roles */}
            {panelVisible('users') && (
              <div style={{
                width: isMobile ? "100%" : "380px",
                minWidth: isMobile ? "100%" : "380px",
                borderRight: isMobile ? "none" : "1px solid #e5e2dc",
                display: "flex",
                flexDirection: "column",
                background: "#fff"
              }}>
                <div style={{ padding: "20px", borderBottom: "1px solid #f0ebe3" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <div style={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "10px",
                        background: "linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}>
                        <Shield size={20} color="#fff" />
                      </div>
                      <div>
                        <h1 style={{ fontSize: "16px", fontWeight: "700", color: "#1f2937", margin: 0 }}>Gestión de Roles</h1>
                        <p style={{ fontSize: "11px", color: "#6b7280", margin: 0 }}>Asignar áreas a usuarios</p>
                      </div>
                    </div>
                    <button
                      onClick={loadRolesData}
                      disabled={loadingRoles}
                      style={{ background: "#f3f4f6", border: "none", borderRadius: "8px", padding: "8px", cursor: "pointer", display: "flex" }}
                    >
                      <RefreshCw size={18} color="#6b7280" style={{ animation: loadingRoles ? 'spin 1s linear infinite' : 'none' }} />
                    </button>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px", marginBottom: "16px" }}>
                    <div style={{ background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)", padding: "12px 10px", borderRadius: "10px", textAlign: "center", border: "1px solid #bfdbfe" }}>
                      <Users size={16} color="#2563eb" />
                      <div style={{ fontSize: "18px", fontWeight: "700", color: "#1e40af" }}>{rolesStats.total}</div>
                      <div style={{ fontSize: "9px", color: "#3b82f6", fontWeight: "500" }}>Total</div>
                    </div>
                    <div style={{ background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)", padding: "12px 10px", borderRadius: "10px", textAlign: "center", border: "1px solid #bbf7d0" }}>
                      <UserCheck size={16} color="#16a34a" />
                      <div style={{ fontSize: "18px", fontWeight: "700", color: "#15803d" }}>{rolesStats.assigned}</div>
                      <div style={{ fontSize: "9px", color: "#22c55e", fontWeight: "500" }}>Asignados</div>
                    </div>
                    <div style={{ background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)", padding: "12px 10px", borderRadius: "10px", textAlign: "center", border: "1px solid #fcd34d" }}>
                      <AlertCircle size={16} color="#d97706" />
                      <div style={{ fontSize: "18px", fontWeight: "700", color: "#b45309" }}>{rolesStats.unassigned}</div>
                      <div style={{ fontSize: "9px", color: "#f59e0b", fontWeight: "500" }}>Sin Rol</div>
                    </div>
                  </div>

                  <select
                    value={filterArea}
                    onChange={(e) => setFilterArea(e.target.value)}
                    style={{ width: "100%", padding: "10px 12px", border: "1px solid #e5e7eb", borderRadius: "10px", fontSize: "13px", background: "#f9fafb", marginBottom: "12px", cursor: "pointer" }}
                  >
                    <option value="all">Todas las áreas</option>
                    <option value="unassigned">Sin asignar</option>
                    {areas.map(area => (
                      <option key={area.id} value={area.id}>{area.name}</option>
                    ))}
                  </select>

                  <div style={{ position: "relative" }}>
                    <Search size={16} color="#9ca3af" style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)" }} />
                    <input
                      type="text"
                      placeholder="Buscar usuarios..."
                      value={rolesSearchTerm}
                      onChange={(e) => setRolesSearchTerm(e.target.value)}
                      style={{ width: "100%", padding: "11px 12px 11px 38px", border: "1px solid #e5e7eb", borderRadius: "10px", fontSize: "13px", boxSizing: "border-box", background: "#f9fafb", outline: "none" }}
                    />
                  </div>
                </div>

                <div style={{ flex: 1, overflowY: "auto" }}>
                  {loadingRoles ? (
                    <div style={{ padding: "60px 20px", textAlign: "center", color: "#9ca3af" }}>
                      <div style={{ width: "32px", height: "32px", border: "3px solid #e5e7eb", borderTopColor: "#7c3aed", borderRadius: "50%", margin: "0 auto 12px", animation: "spin 1s linear infinite" }} />
                      Cargando usuarios...
                    </div>
                  ) : filteredRolesUsers.length === 0 ? (
                    <div style={{ padding: "60px 20px", textAlign: "center", color: "#9ca3af" }}>
                      <Users size={32} style={{ marginBottom: "12px", opacity: 0.4 }} />
                      <div>No hay usuarios</div>
                    </div>
                  ) : (
                    filteredRolesUsers.map(user => (
                      <div
                        key={user.uid}
                        onClick={() => { setSelectedRoleUser(user); if (isMobile) setMobileView('details'); }}
                        style={{
                          padding: "14px 20px",
                          borderBottom: "1px solid #f3f4f6",
                          cursor: "pointer",
                          background: selectedRoleUser?.uid === user.uid ? "#f5f3ff" : "transparent",
                          display: "flex",
                          alignItems: "center",
                          gap: "12px"
                        }}
                      >
                        <div style={{
                          width: "44px",
                          height: "44px",
                          borderRadius: "12px",
                          background: user.photoURL ? `url(${user.photoURL}) center/cover` : getAvatarColor(user.uid),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          fontSize: "15px",
                          fontWeight: "600"
                        }}>
                          {!user.photoURL && getInitials(user.displayName)}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: "14px", fontWeight: "600", color: "#1f2937", marginBottom: "3px" }}>
                            {user.displayName || "Sin nombre"}
                          </div>
                          <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "4px" }}>{user.email}</div>
                          <div style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            background: user.areaId ? `${getAreaColor(user.areaId)}15` : "#f3f4f6",
                            color: user.areaId ? getAreaColor(user.areaId) : "#9ca3af",
                            padding: "4px 10px",
                            borderRadius: "6px",
                            fontSize: "11px",
                            fontWeight: "500",
                            border: `1px solid ${user.areaId ? `${getAreaColor(user.areaId)}30` : "#e5e7eb"}`
                          }}>
                            <Building2 size={12} />
                            {user.areaName || "Sin asignar"}
                          </div>
                        </div>
                        <ChevronRight size={18} color="#d1d5db" />
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Panel 2: Asignación de área */}
            {panelVisible('details') && (
              <div style={{ flex: 1, display: "flex", flexDirection: "column", background: "#fbf9f4", width: isMobile ? "100%" : "auto" }}>
                {!selectedRoleUser ? (
                  <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", color: "#9ca3af", padding: "40px" }}>
                    <Shield size={56} style={{ marginBottom: "16px", opacity: 0.2 }} />
                    <div style={{ textAlign: "center", fontSize: "14px", color: "#6b7280" }}>Selecciona un usuario para asignar área</div>
                  </div>
                ) : (
                  <>
                    <div style={{ padding: "24px", background: "#fff", borderBottom: "1px solid #e5e2dc" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                        {isMobile && (
                          <button onClick={goBack} style={{ background: "none", border: "none", cursor: "pointer", padding: "4px", display: "flex" }}>
                            <ChevronLeft size={24} color="#6b7280" />
                          </button>
                        )}
                        <div style={{
                          width: "64px",
                          height: "64px",
                          borderRadius: "16px",
                          background: selectedRoleUser.photoURL ? `url(${selectedRoleUser.photoURL}) center/cover` : getAvatarColor(selectedRoleUser.uid),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          fontSize: "22px",
                          fontWeight: "600",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
                        }}>
                          {!selectedRoleUser.photoURL && getInitials(selectedRoleUser.displayName)}
                        </div>
                        <div>
                          <h2 style={{ fontSize: "20px", fontWeight: "700", color: "#1f2937", margin: 0 }}>
                            {selectedRoleUser.displayName || "Sin nombre"}
                          </h2>
                          <p style={{ fontSize: "14px", color: "#6b7280", margin: "4px 0" }}>{selectedRoleUser.email}</p>
                        </div>
                      </div>

                      <div style={{
                        marginTop: "20px",
                        padding: "16px",
                        background: selectedRoleUser.areaId ? `${getAreaColor(selectedRoleUser.areaId)}10` : "#f9fafb",
                        borderRadius: "12px",
                        border: `1px solid ${selectedRoleUser.areaId ? `${getAreaColor(selectedRoleUser.areaId)}30` : "#e5e7eb"}`
                      }}>
                        <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "8px" }}>Área actual:</div>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <div style={{
                              width: "36px",
                              height: "36px",
                              borderRadius: "10px",
                              background: selectedRoleUser.areaId ? getAreaColor(selectedRoleUser.areaId) : "#9ca3af",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center"
                            }}>
                              <Building2 size={18} color="#fff" />
                            </div>
                            <span style={{ fontSize: "16px", fontWeight: "600", color: selectedRoleUser.areaId ? getAreaColor(selectedRoleUser.areaId) : "#6b7280" }}>
                              {selectedRoleUser.areaName || "Sin asignar"}
                            </span>
                          </div>
                          {selectedRoleUser.areaId && (
                            <button
                              onClick={() => removeRole(selectedRoleUser.uid)}
                              disabled={savingRole}
                              style={{
                                background: "#fee2e2",
                                color: "#dc2626",
                                border: "1px solid #fecaca",
                                borderRadius: "8px",
                                padding: "8px 12px",
                                fontSize: "12px",
                                fontWeight: "500",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                opacity: savingRole ? 0.6 : 1
                              }}
                            >
                              <X size={14} />
                              Quitar rol
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    <div style={{ flex: 1, overflowY: "auto", padding: "24px" }}>
                      <h3 style={{ fontSize: "14px", fontWeight: "600", color: "#374151", marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
                        <Building2 size={16} />
                        Seleccionar área ({areas.length} disponibles)
                      </h3>

                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "12px" }}>
                        {loadingAreas ? (
                          <div style={{ gridColumn: "1/-1", textAlign: "center", padding: "40px", color: "#9ca3af" }}>Cargando áreas...</div>
                        ) : (
                          areas.map(area => (
                            <button
                              key={area.id}
                              onClick={() => assignArea(selectedRoleUser.uid, area.id)}
                              disabled={savingRole || selectedRoleUser.areaId === area.id}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                padding: "12px 14px",
                                background: selectedRoleUser.areaId === area.id ? `${getAreaColor(area.id)}15` : "#fff",
                                border: `2px solid ${selectedRoleUser.areaId === area.id ? getAreaColor(area.id) : "#e5e7eb"}`,
                                borderRadius: "10px",
                                cursor: selectedRoleUser.areaId === area.id ? "default" : "pointer",
                                textAlign: "left",
                                opacity: savingRole ? 0.6 : 1,
                                transition: "all 0.2s"
                              }}
                            >
                              <div style={{
                                width: "36px",
                                height: "36px",
                                borderRadius: "8px",
                                background: `${getAreaColor(area.id)}20`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0
                              }}>
                                <Building2 size={18} color={getAreaColor(area.id)} />
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: "12px", fontWeight: "600", color: "#1f2937" }}>{area.name}</div>
                                <div style={{ fontSize: "10px", color: "#9ca3af", textTransform: "capitalize" }}>
                                  {area.accessLevel}
                                </div>
                              </div>
                              {selectedRoleUser.areaId === area.id && (
                                <Check size={16} color={getAreaColor(area.id)} />
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
